import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Ticket, CheckCircle2 } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { listReferralRedemptions, fulfillReferralRedemption } from "../../services/referralAdmin";

const STATUS_STYLES = {
  ISSUED: "bg-amber-50 text-amber-700 border-amber-200",
  FULFILLED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CANCELLED: "bg-rose-50 text-rose-600 border-rose-200",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "ISSUED", label: "To Fulfill" },
  { key: "FULFILLED", label: "Fulfilled" },
];

const money = (v) => (v === null || v === undefined || v === "" ? "—" : `₹${v}`);

/**
 * Zumale-admin vendor-voucher redemption fulfillment queue (v3_65). Buyers redeem reward balance
 * against reward vendors → a voucher code is issued; Zumale settles with the vendor offline and marks
 * the voucher FULFILLED here. (Interior-execution credits are applied automatically, not queued.)
 */
const ReferralRedemptions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [confirm, setConfirm] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const res = await listReferralRedemptions(params);
      setRows(Array.isArray(res) ? res : res?.items || []);
    } catch (e) {
      toast.error("Failed to load redemptions");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const doFulfill = async () => {
    const r = confirm;
    setConfirm(null);
    if (!r) return;
    setSavingId(r.id);
    try {
      await fulfillReferralRedemption(r.id);
      toast.success("Voucher marked fulfilled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update redemption");
    } finally {
      setSavingId(null);
    }
  };

  const columns = [
    {
      key: "voucher",
      header: "Voucher",
      width: 160,
      render: (r) => <span className="font-mono text-sm font-semibold text-slate-900">{r.voucherCode || "—"}</span>,
    },
    {
      key: "buyer",
      header: "Buyer",
      width: 200,
      render: (r) => <span className="text-sm text-slate-700">{r.referrerName || `User #${r.referrerUserId}`}</span>,
    },
    {
      key: "vendor",
      header: "Reward Partner",
      width: 200,
      render: (r) => <span className="text-sm text-slate-700">{r.referralVendorName || "—"}</span>,
    },
    { key: "amount", header: "Amount", width: 120, render: (r) => <span className="text-sm font-semibold text-slate-900">{money(r.amount)}</span> },
    {
      key: "status",
      header: "Status",
      width: 130,
      render: (r) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${STATUS_STYLES[r.status] || STATUS_STYLES.ISSUED}`}>
          {r.status}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Fulfill",
      width: 150,
      align: "center",
      render: (r) => (
        <button
          disabled={savingId === r.id || r.status !== "ISSUED"}
          onClick={() => setConfirm(r)}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border transition-colors disabled:opacity-50 bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
        >
          <CheckCircle2 size={14} /> Mark Fulfilled
        </button>
      ),
    },
  ];

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Ticket}
        title="Referral Redemptions"
        subtitle="Vendor voucher redemptions to settle. Mark a voucher fulfilled once you've settled with the reward partner."
      />

      <div className="flex items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
              filter === f.key
                ? "bg-flamingo-600 text-white border-flamingo-600"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-5">
        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          minWidth={880}
          rowKey={(r, i) => r?.id ?? i}
          emptyState={
            <div className="px-6 py-12 text-center">
              <Ticket size={28} className="mx-auto text-slate-300" />
              <p className="text-base font-semibold text-slate-900 mt-2">No voucher redemptions yet</p>
              <p className="text-sm text-slate-500 mt-1">Vendor vouchers appear here when buyers redeem their rewards.</p>
            </div>
          }
        />
      </div>

      <ConfirmationModal
        isOpen={!!confirm}
        onConfirm={doFulfill}
        onCancel={() => setConfirm(null)}
        message={`Mark voucher ${confirm?.voucherCode || ""} (${money(confirm?.amount)}) as fulfilled? Do this once you've settled with ${confirm?.referralVendorName || "the vendor"}.`}
      />
    </div>
  );
};

export default ReferralRedemptions;
